import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  Checkbox,
  CheckboxGroup,
  Inline,
  Select,
  Toggle,
} from 'chaoskit/src/components';
import { config } from 'chaoskit/src/helpers/config';

import { Icon } from '../../components';
import KubernetesGraph from './KubernetesGraph';

const K8sSection = ({ label, selection, params, children, ...props }) => {
  const activeCount = selection.filter(item => params.includes(item)).length;

  return (
    <div className="product__k8s-targets-section" {...props}>
      <div className="product__k8s-target-header">
        <div className="product__k8s-target-header-text">{label}</div>
        <div className="product__k8s-target-count--fill">
          {activeCount} of {params.length} selected
        </div>
      </div>
      {children}
    </div>
  );
};

K8sSection.propTypes = {
  label: PropTypes.string.isRequired,
  selection: PropTypes.array.isRequired,
  params: PropTypes.array.isRequired,
  children: PropTypes.node.isRequired,
};

const K8sCheckbox = ({ label, handleSelection, selection, pills }) => (
  <div className="product__k8s-target-checkboxWrapper">
    <Checkbox
      name={label}
      onChange={handleSelection}
      label={label}
      value={label}
      checked={selection.includes(label)}
      className="u-textSmall"
    />
    {pills.length > 0 && (
      <div>
        <Inline size="small">
          {pills.map(pill => (
            <div
              key={pill}
              className={cx('product__k8s-target-count--default', {
                [config.classes.active]: selection.includes(label),
              })}
            >
              {pill}
            </div>
          ))}
        </Inline>
      </div>
    )}
  </div>
);

K8sCheckbox.propTypes = {
  label: PropTypes.string.isRequired,
  pills: PropTypes.array.isRequired,
  selection: PropTypes.array.isRequired,
  handleSelection: PropTypes.func.isRequired,
};

const KubernetesUI = () => {
  const [selection, setSelection] = useState([]);
  const objects = [
    'Deployment-1',
    'Deployment-2',
    'StatefulSet-1',
    'DaemonSet-1',
    'DaemonSet-2',
  ];

  const handleSelection = (name, value, checked) => {
    setSelection(currentSelections => {
      const selectionsSet = new Set(currentSelections);

      if (!checked) {
        selectionsSet.delete(name);
        return Array.from(selectionsSet);
      }

      selectionsSet.add(name);
      return Array.from(selectionsSet);
    });
  };

  const handleTargetAll = (name, value, checked) => {
    if (checked) {
      setSelection(objects);
    } else {
      setSelection([]);
    }
  };

  return (
    <div className="product__k8s-ui-wrapper">
      <div className="product__k8s-ui">
        <header className="product__k8s-header">
          <Icon
            icon="kubernetes"
            className="product__k8s-header-logo u-textPrimary"
          />
          <h4 className="product__k8s-header-text">Choose objects to target</h4>
        </header>
        <div className="product__k8s-selections">
          <div>
            <Select
              label="1. Choose a cluster"
              name="cluster"
              options={[{ value: 'myCluster', label: 'myCluster' }]}
              required
              className="product__k8s-select"
              onChange={() => {}}
            />
          </div>
          <div>
            <Select
              label="2. Choose a namespace"
              name="namespace"
              options={[{ value: 'staging', label: 'Staging' }]}
              className="product__k8s-select"
              onChange={() => {}}
            />
          </div>
        </div>
        <div className="product__k8s-filters">
          <div className="product__k8s-targets">
            <div className="product__k8s-targets-section">
              <Toggle
                name="targetAll"
                label="Target all objects"
                className="product__k8s-targets-all"
                onChange={handleTargetAll}
                checked={selection.length === objects.length}
              />
            </div>
            <K8sSection
              label="Deployments"
              selection={selection}
              params={['Deployment-1', 'Deployment-2']}
            >
              <CheckboxGroup>
                <K8sCheckbox
                  label="Deployment-1"
                  pills={['1 ReplicaSet', '1 Pod']}
                  handleSelection={handleSelection}
                  selection={selection}
                />
                <K8sCheckbox
                  label="Deployment-2"
                  pills={['7 Pods']}
                  handleSelection={handleSelection}
                  selection={selection}
                />
              </CheckboxGroup>
            </K8sSection>
            <K8sSection
              label="StatefulSets"
              selection={selection}
              params={['StatefulSet-1']}
            >
              <CheckboxGroup>
                <K8sCheckbox
                  label="StatefulSet-1"
                  pills={['2 Pods']}
                  handleSelection={handleSelection}
                  selection={selection}
                />
              </CheckboxGroup>
            </K8sSection>
            <K8sSection
              label="DaemonSets"
              selection={selection}
              params={['DaemonSet-1', 'DaemonSet-2']}
            >
              <CheckboxGroup>
                <K8sCheckbox
                  label="DaemonSet-1"
                  pills={['1 Pod']}
                  handleSelection={handleSelection}
                  selection={selection}
                />
                <K8sCheckbox
                  label="DaemonSet-2"
                  pills={['1 Pod']}
                  handleSelection={handleSelection}
                  selection={selection}
                />
              </CheckboxGroup>
            </K8sSection>
          </div>
          <div className="product__k8s-graph">
            <div className="product__k8s-graph-header">
              <h5 className="u-textXsmall u-textUppercase u-mb--remove">
                Blast Radius
              </h5>
              <div className="u-textHeading u-textLarge u-mt--xsmall">
                {selection.length} of 5
              </div>
            </div>

            <KubernetesGraph
              className="product__k8s-graph-svg"
              selection={selection}
            />

            <div className="product__k8s-legend">
              <div className="product__k8s-legend--deployment">Deployment</div>
              <div className="product__k8s-legend--statefulset">
                StatefulSet
              </div>
              <div className="product__k8s-legend--daemonset">DaemonSet</div>
              <div className="product__k8s-legend--replicaset">ReplicaSet</div>
              <div className="product__k8s-legend--pod">Pod</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KubernetesUI;
